<script setup lang="ts">
import { UI_CHAMP_PUBLIC } from "~/utils/stylesChamps";
/**
 * Saisie code promo + état "appliqué" avec transition.
 * Partagé entre /a/[slug] (variant "a", input size md, bouton noir) et /b/[slug]
 * (variant "b", input size sm, bouton "accent-btn").
 */
import type { PromoAppliedModel } from "~/types/public-page";

// Montants et dates dans la langue de la page.
const { formatCurrency } = useFormatLangue();

const props = defineProps<{
  modelValue: string;
  applied: PromoAppliedModel | null;
  loading: boolean;
  error: string;
  variant: 'a' | 'b';
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
  (e: 'apply' | 'remove'): void;
}>();

const code = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
});

const inputUi = computed(() => props.variant === 'a'
  ? { base: `${UI_CHAMP_PUBLIC} rounded-xl! uppercase! h-10! bg-white! text-gray-900! placeholder:text-gray-400!` }
  : { base: 'rounded-lg! uppercase! h-9! bg-white! text-gray-900! placeholder:text-gray-400! ring-1! ring-gray-200! focus:ring-1! focus:ring-black!' },
);

const buttonClass = computed(() => props.variant === 'a'
  ? 'shrink-0 h-10 px-5 rounded-xl text-sm font-semibold bg-[#212121] text-white hover:opacity-90 transition-opacity disabled:opacity-40'
  : 'shrink-0 h-9 px-4 rounded-lg text-xs font-semibold bg-[#212121] text-white accent-btn hover:opacity-90 transition-opacity disabled:opacity-40',
);

const appliedHeightClass = computed(() => props.variant === 'a' ? 'h-10' : '');
</script>

<template>
  <Transition
    mode="out-in"
    enter-active-class="transition-all duration-300 ease-out"
    enter-from-class="opacity-0 scale-95 translate-y-1"
    enter-to-class="opacity-100 scale-100 translate-y-0"
    leave-active-class="transition-all duration-200 ease-in"
    leave-from-class="opacity-100 scale-100 translate-y-0"
    leave-to-class="opacity-0 scale-95 -translate-y-1"
  >
    <div v-if="applied" key="applied" class="flex items-center justify-between" :class="appliedHeightClass">
      <div class="flex items-center gap-2.5" :class="variant === 'b' ? 'gap-2 min-w-0' : ''">
        <div
          class="rounded-full bg-[#34C759] flex items-center justify-center"
          :class="variant === 'a' ? 'w-6 h-6' : 'w-5 h-5 shrink-0'"
        >
          <UIcon name="i-lucide-check" :class="variant === 'a' ? 'size-3.5 text-white' : 'size-3 text-white'" />
        </div>
        <span class="text-sm font-semibold text-[#2d2e5c]" :class="variant === 'b' ? 'truncate' : ''">{{ applied.code }}</span>
        <span class="text-sm text-[#34C759] font-medium" :class="variant === 'b' ? 'shrink-0' : ''">
          -{{ applied.discount_type === 'percentage' ? `${applied.discount_value}%` : formatCurrency(applied.discount_value) }}
        </span>
      </div>
      <button
        class="text-xs font-medium text-red-500 hover:text-red-600 transition-colors"
        :class="variant === 'b' ? 'shrink-0 ml-2' : ''"
        @click="$emit('remove')"
      >
        {{ $t("association.publicPage.supprimer") }}
      </button>
    </div>
    <div v-else key="input">
      <div class="flex items-center gap-2">
        <UInput
          v-model="code"
          :placeholder="$t('association.publicPage.codePromo')"
          :size="variant === 'a' ? 'md' : 'sm'"
          class="flex-1 uppercase"
          :ui="inputUi"
          @keyup.enter="$emit('apply')"
        />
        <button
          :class="buttonClass"
          :disabled="!code.trim() || loading"
          @click="$emit('apply')"
        >
          <UIcon v-if="loading" name="i-lucide-loader-2" class="size-3.5 animate-spin" />
          <span v-else>{{ $t("association.publicPage.appliquer") }}</span>
        </button>
      </div>
      <p v-if="error" class="text-xs text-red-500 mt-1">{{ error }}</p>
    </div>
  </Transition>
</template>
